import {
  Injector,
  enableProdMode,
  importProvidersFrom,
  provideAppInitializer,
} from '@angular/core';

import {
  provideHttpClient,
  withInterceptorsFromDi,
} from '@angular/common/http';
import { MatBottomSheetModule } from '@angular/material/bottom-sheet';
import { DateAdapter, MAT_DATE_FORMATS } from '@angular/material/core';
import { MatDialogModule } from '@angular/material/dialog';
import { MatPaginatorIntl } from '@angular/material/paginator';
import { MatSnackBarModule } from '@angular/material/snack-bar';
import { BrowserModule, bootstrapApplication } from '@angular/platform-browser';
import { provideAnimations } from '@angular/platform-browser/animations';
import {
  TranslateCompiler,
  TranslateLoader,
  TranslateModule,
} from '@ngx-translate/core';
import { NgxsDispatchPluginModule } from '@ngxs-labs/dispatch-decorator';
import { NgxsReduxDevtoolsPluginModule } from '@ngxs/devtools-plugin';
import { NgxsLoggerPluginModule } from '@ngxs/logger-plugin';
import { NgxsModule } from '@ngxs/store';
import { OktaAuthModule } from '@okta/okta-angular';
import { OktaAuth, TokenParams } from '@okta/okta-auth-js';
import {
  getInitConfigFactory,
  initAccessibilityObserversFactory,
} from '@sc/data-access-app-injector';
import { AssetsService } from '@sc/data-access-assets';
import { DataAccessEnvironmentModule } from '@sc/data-access-environment';
import {
  CLIENT_APP_ID,
  HIGHLIGHT_REQUIRED_INPUTS,
} from '@sc/data-access-models';
import {
  AppointmentsFilterState,
  AppointmentsState,
  CTAQueryState,
  DossierState,
  DotFittersState,
  FilesState,
  FittersState,
  NavigationState,
  ScoresState,
} from '@sc/data-access-state-management';
import {
  CustomLangLoaderService,
  CustomMatPaginatorIntl,
  LocaleProvider,
  TabTitleStrategyService,
} from '@sc/data-access-translation';
import { FeatureAuthModule } from '@sc/feature-auth-okta';
import {
  AgendaEventsState,
  DossiersRequestedAppointmentState,
  DossiersToValidateState,
} from '@sc/feature-fitter-planning';
import { AgendaResourcesState } from '@sc/feature-fitter-shared-libs';
import { TAG_MANAGER } from '@sc/feature-tag-manager';
import { ThemesManagerService } from '@sc/feature-themes-manager';
import {
  CUSTOM_DATE_FORMATS,
  DayjsMaterialDateAdapterService,
} from '@sc/ui-components';
import { TranslateMessageFormatCompiler } from 'ngx-translate-messageformat-compiler';
import { of } from 'rxjs';
import { AppRoutingModule } from './app/app-routing.module';
import { AppComponent } from './app/app.component';
import { GraphQLModule } from './app/graphql.module';
import { environment } from './environments/environment';
import { Router } from '@angular/router';

const oktaAuth = new OktaAuth(environment.auth);

if (environment.production) {
  enableProdMode();
}

bootstrapApplication(AppComponent, {
  providers: [
    importProvidersFrom(
      BrowserModule,
      AppRoutingModule,
      DataAccessEnvironmentModule.forRoot(environment),
      OktaAuthModule.forRoot({
        oktaAuth,
        onAuthRequired: (
          oktaAuth: OktaAuth,
          injector: Injector,
          options?: TokenParams
        ) => {
          const router = injector.get(Router);
          router.navigate(['login']);
        },
      }),

      FeatureAuthModule.forRoot({
        redirectAfterLogin: '/planning',
        redirectAfterLogout: '',
      }),

      GraphQLModule,
      NgxsModule.forRoot(
        [
          FittersState,
          CTAQueryState,
          NavigationState,
          ScoresState,
          AppointmentsState,
          DossierState,
          AppointmentsFilterState,
          FilesState,
          DotFittersState,
        ],
        {
          developmentMode:
            String(environment.log.logActivationStateManagement) === 'true',
        }
      ),
      NgxsDispatchPluginModule.forRoot(),
      NgxsLoggerPluginModule.forRoot({
        disabled:
          String(environment.log.logActivationStateManagement) !== 'true',
      }),
      NgxsReduxDevtoolsPluginModule.forRoot({
        disabled:
          String(environment.log.logActivationStateManagement) !== 'true',
      }),
      TranslateModule.forRoot({
        compiler: {
          provide: TranslateCompiler,
          useClass: TranslateMessageFormatCompiler,
        },
        loader: { provide: TranslateLoader, useClass: CustomLangLoaderService },
      }),
      NgxsModule.forFeature([
        AgendaEventsState,
        DossiersToValidateState,
        DossiersRequestedAppointmentState,
        AgendaResourcesState,
      ]),
      MatSnackBarModule,
      MatDialogModule,
      MatBottomSheetModule
    ),
    LocaleProvider,
    { provide: CLIENT_APP_ID, useValue: 'fitter' },
    { provide: HIGHLIGHT_REQUIRED_INPUTS, useValue: true },
    provideAppInitializer(() => {
      const initializerFn = getInitConfigFactory(function (injector: Injector) {
        // Force init of following services
        injector.get(AssetsService);
        injector.get(ThemesManagerService);
        // Remove tabTitleStrategy after its refacto in angular 14, until then we want it to be eagerly loaded at app init
        injector.get(TabTitleStrategyService);
        return of(null);
      })();
      return initializerFn();
    }),
    provideAppInitializer(() => {
      const initializerFn = initAccessibilityObserversFactory();
      return initializerFn();
    }),
    {
      provide: TAG_MANAGER,
      useValue: [],
    },
    { provide: MatPaginatorIntl, useClass: CustomMatPaginatorIntl },
    {
      provide: DateAdapter,
      useClass: DayjsMaterialDateAdapterService,
    },
    {
      provide: MAT_DATE_FORMATS,
      useValue: CUSTOM_DATE_FORMATS,
    },
    provideAnimations(),
    provideHttpClient(withInterceptorsFromDi()),
  ],
}).catch((err) => console.error(err));
